import { memo } from "react";
import { MenuItems } from "./types";
import { StorageScope } from "../../services/api/instances/storageApi/types/service_types";
import { TEntityInfo } from "../types/utility";
import { useStorageSelectors } from "../../app/state/storageObjectSlice/hook/use-storage-selectors";
import { useSidebarHeader } from "./hooks/use-sidebar-header";
import { useSidebarObject } from "./hooks/use-sidebar-object";
import { useMenuSelectors } from "../../app/state/menuSlice/hook/use-menu-selectors";
import { DeleteEntityModal } from "../Components/Modals/components/DeleteEntityModal";
import { ObjectManagerModal } from "../Components/Modals/components/ObjectManagerModal";
import { useAppModals } from "../../app/state/modalSlice/hook/use-app-modals";
import { useAppMenus } from "../../app/state/menuSlice/hook/use-app-menus";
import { useTransferObject } from "../Components/Modals/hook/use-transfer-object";
import { useRangeSelection } from "./hooks/use-range-selection";
import { SidebarResizableWrapper } from "./Components/SidebarResizableWrapper";
import { SidebarHeader } from "./Components/SidebarHeader";
import { SidebarAddButton } from "./Components/SidebarHeader/SidebarAddButton";
import { SidebarList } from "./Components/SidebarList";
import { SidebarFooter } from "./Components/SidebarFooter";
import { ChatModal } from "./Components/ChatModal";
import { AudioTranscription } from "../../features/Modals/AudioTranscription";
import { SidebarActionsMenu } from "./Components/SidebarActionsMenu";
import { SIDEBAR_ITEM_ACTIONS } from "./constants";
import { SidebarTransferMenu } from "./Components/SidebarTransferMenu";
import { ShareObjectModal } from "./Components/ShareObjectModal";
import { ChatWithAgentModal } from "../../features/DeerFlow/Components/ChatWithAgentModal";

interface Props {
  menuItems: MenuItems;
  selectedId: string;
  service: StorageScope;
  onSelect: (id: string) => void;
  onAdd?: () => void;
  customEditObject?: (info: TEntityInfo | null) => void;
  customCreateObject?: (info: TEntityInfo | null) => void;
  extraAction?: React.ReactElement;
  withoutAddFolderButton?: boolean;
  isLoading?: boolean;
  title?: string;
  withoutActions?: boolean;
  defaultName?: string;
}

const SidebarImmer = (props: Props) => {
  const { objects } = useStorageSelectors(props.service);
  const {
    ui,
    resultItems,
    searchQuery,
    isOpenSearchPanel,
    selectedItem,
    handleSelectSidebarItem,
    handleSidebarHeaderActions,
  } = useSidebarHeader(props.menuItems, props.onSelect, props.service);
  const { folderIds, handleFolderClick, handleSidebarActions } =
    useSidebarObject(
      props.selectedId,
      props.menuItems,
      props.service,
      props.customEditObject,
      props.customCreateObject,
    );
  const sidebarMenu = useMenuSelectors("sidebar_item_actions_menu");
  const { handleOpenModal, handleCloseModal } = useAppModals();
  const { handleOpenMenu, handleCloseMenu, handleOpenSubMenu } = useAppMenus();
  const { selectedIds, handleItemClick, reset } = useRangeSelection(
    props.menuItems,
    props.onSelect,
    handleOpenMenu,
    props.selectedId,
  );
  const sidebarAction = useMenuSelectors("sidebar_item_actions_menu");
  const { folderId, handleTransfer } = useTransferObject(
    props.service,
    sidebarAction,
    objects,
    reset,
  );
  return (
    <SidebarResizableWrapper
      service={props.service}
      isSidebarOpen={ui?.sidebar?.isOpen ?? true}
      minWidth={260}
      maxWidth={500}
    >
      <SidebarHeader
        menuItems={props.menuItems}
        menuItem={selectedItem}
        addButton={
          <SidebarAddButton
            onAdd={props.onAdd}
            service={props.service}
            handleCloseMenu={handleCloseMenu}
            handleOpenAddFolderModal={() =>
              handleOpenModal("object_manager", null, "create_folder_object")
            }
            isSidebarOpen={ui?.sidebar?.isOpen ?? true}
            handleOpenAddMenu={handleOpenMenu}
            withoutAddFolderButton={props.withoutAddFolderButton}
            title={props.title}
          />
        }
        onSelectItem={handleSelectSidebarItem}
        service={props.service}
        searchQuery={searchQuery}
        isSearchPanelOpen={isOpenSearchPanel}
        handleActions={handleSidebarHeaderActions}
        extraAction={props.extraAction}
        isLoading={props.isLoading}
        isSidebarOpen={ui?.sidebar?.isOpen ?? true}
      />
      {ui?.sidebar?.isOpen && (
        <SidebarList
          menuItems={resultItems}
          onSelect={handleItemClick}
          selectedId={props.selectedId}
          handleItemActions={handleOpenMenu}
          handleFolderClick={handleFolderClick}
          folderIds={folderIds}
          withOutActions={props.withoutActions}
          service={props.service}
          selectedIds={selectedIds}
        />
      )}

      <SidebarFooter
        handleActions={handleSidebarActions}
        isSidebarOpen={ui?.sidebar?.isOpen ?? true}
      />
      <ChatModal closeModal={handleCloseModal} />
      <AudioTranscription closeModal={handleCloseModal} />
      <ObjectManagerModal
        service={props.service}
        closeModal={handleCloseModal}
        defaultName={props.defaultName}
      />
      <ChatWithAgentModal closeModal={handleCloseModal} />
      <DeleteEntityModal
        service={props.service}
        closeModal={handleCloseModal}
        subtype={"delete_object"}
      />
      <SidebarActionsMenu
        service={props.service}
        actions={SIDEBAR_ITEM_ACTIONS}
        subtype={sidebarMenu.info?.extra ?? ""}
        handleActionClick={handleSidebarActions}
        handleOpenSubMenu={handleOpenSubMenu}
        closeMenu={handleCloseMenu}
        folders={props.menuItems.filter((item) => item.value === null)}
        handleTransfer={handleTransfer}
        currentFolderId={folderId}
        isReceivedObject={sidebarMenu.info?.metadata?.isReceivedObject}
        readOnly={sidebarMenu?.info?.metadata?.readonly}
      />
      <SidebarTransferMenu
        folders={props.menuItems.filter((m) => m.value === null)}
        handleTransfer={(id) => handleTransfer(id, selectedIds)}
        currentFolderId={props.selectedId}
        reset={reset}
        subtype={"sidebar"}
        closeMenu={handleCloseMenu}
        leftPos={240}
      />
      <ShareObjectModal service={props.service} closeModal={handleCloseModal} />
    </SidebarResizableWrapper>
  );
};

export const Sidebar = memo(SidebarImmer);
